// File: frontend/src/components/admin/AdminRankBoard.tsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  FaTrophy,
  FaBook,
  FaChevronDown,
  FaChevronUp,
  FaThumbsUp,
  FaThumbsDown,
  FaStar,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { RankBoardEntryDto, RankScoringConfigDto } from "../../types/rankBoard.types";
import { getRookieBoard, getAuthorBoard, getRankScoringConfig } from "../../services/adminRankBoardService";
import { generateProfileUrl } from "../../utils/slugGenerator";
import { formatAdminDate, formatRefreshTime } from "../../utils/adminUtils";
import { AdminRefreshButton } from "./common";
import "./AdminRankBoard.css";

interface AdminRankBoardProps {
  isAdmin: boolean;
  /**
   * Which ranked board to show. "rookies" (default) lists ROOKIE users;
   * "authors" lists AUTHOR users with the exact same scoring, columns and
   * documentation. The only functional difference lives outside this
   * component: author ranks are never shown on profiles.
   */
  mode?: "rookies" | "authors";
}

// ── Column sorting ──────────────────────────────────────────────────────
// Every sortable field maps directly to a RankBoardEntryDto key so it can
// be used as a property accessor when comparing rows.
type SortDirection = "asc" | "desc";
type SortColumnType = "number" | "date" | "string";

type SortField =
  | "rank"
  | "score"
  | "username"
  | "registrationDate"
  | "totalEntryCount"
  | "avgEntryLength"
  | "upvotes"
  | "downvotes"
  | "favorites"
  | "totalLoginDays"
  | "currentStreak"
  | "onlineRatio"
  | "reportCount"
  | "adminEditedCount"
  | "adminDeletedCount"
  | "topicSuggestionCount"
  | "topicSuggestionRejectedCount"
  | "topicSuggestionApprovalRate";

// Column type per sortable field — drives both the comparator below and
// the "natural" first-click direction (see defaultSortDirection).
const SORT_COLUMN_TYPES: Record<SortField, SortColumnType> = {
  rank: "number",
  score: "number",
  username: "string",
  registrationDate: "date",
  totalEntryCount: "number",
  avgEntryLength: "number",
  upvotes: "number",
  downvotes: "number",
  favorites: "number",
  totalLoginDays: "number",
  currentStreak: "number",
  onlineRatio: "number",
  reportCount: "number",
  adminEditedCount: "number",
  adminDeletedCount: "number",
  topicSuggestionCount: "number",
  topicSuggestionRejectedCount: "number",
  topicSuggestionApprovalRate: "number",
};

/**
 * First-click direction per column type, matching the expected reading
 * order for each kind of value:
 * - number -> descending (highest first, e.g. top score / most votes)
 * - date   -> ascending  (oldest first, e.g. earliest registration)
 * - string -> ascending  (A -> Z)
 * Clicking the already-active column again simply flips asc <-> desc.
 */
const defaultSortDirection = (type: SortColumnType): SortDirection =>
  type === "number" ? "desc" : "asc";

/**
 * Admin "Çaylaklar" (Rookies) panel.
 *
 * Lists every user still in the ROOKIE role, ranked by the promotion score
 * computed in backend RankScoreCalculator. Admins pick authors from the
 * top of this board. Users promoted to AUTHOR disappear automatically on the
 * next load/refresh because the backend filters on role = ROOKIE.
 *
 * The "Puanlama Yöntemi" documentation block renders the ACTUAL coefficients
 * fetched from /api/admin/rookies/scoring-config, so the on-screen docs can
 * never drift from the code. Visible to both ADMIN and MODERATOR.
 *
 * Layout notes:
 * - Column order: Rank → SCORE → User → metrics (score sits next to rank).
 * - Headers are compact abbreviations; every th carries a title tooltip
 *   with the full column name.
 * - The horizontal scrollbar is rendered on TOP of the table (rotateX trick)
 *   so it is reachable without scrolling to the bottom of a long list.
 * - Search filter is always visible (like other admin panels) and filters
 *   client-side by username.
 * - Every column header is sortable (client-side, same FaSort/FaSortUp/
 *   FaSortDown icons used across the other admin panels). First click uses
 *   the natural direction for that value's type — numbers highest first,
 *   dates oldest first, text A→Z — and a second click on the same column
 *   flips it. The votes header combines 3 metrics, so its 3 icons each
 *   sort independently.
 */
export const AdminRankBoard: React.FC<AdminRankBoardProps> = ({ isAdmin, mode = "rookies" }) => {
  const { t } = useTranslation();
  // Mode-specific i18n keys — everything else is shared between the boards
  const isAuthors = mode === "authors";
  const titleKey = isAuthors ? "admin_authors_title" : "admin_rookies_title";
  const countKey = isAuthors ? "admin_authors_count" : "admin_rookies_count";
  const emptyKey = isAuthors ? "admin_authors_empty" : "admin_rookies_empty";
  const errorKey = isAuthors ? "admin_authors_error" : "admin_rookies_error";
  const loadingKey = isAuthors ? "admin_authors_loading" : "admin_rookies_loading";
  const navigate = useNavigate();

  const [rookies, setRookies] = useState<RankBoardEntryDto[]>([]);
  const [config, setConfig] = useState<RankScoringConfigDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [docOpen, setDocOpen] = useState<boolean>(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  // Username search — filter bar is open by default like other admin panels
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Column sort — defaults to "rank" ascending, i.e. exactly the order the
  // backend already returns (score descending -> 1-based rank assigned).
  const [sortField, setSortField] = useState<SortField>("rank");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  // Load (or reload) the ranked board — the backend always computes fresh data
  const loadBoard = useCallback(async (isRefresh: boolean) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const data = isAuthors ? await getAuthorBoard() : await getRookieBoard();
      setRookies(data);
      setLastRefreshedAt(new Date());
    } catch (err) {
      console.error("Failed to load rookie board:", err);
      setError(t(errorKey));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t, isAuthors, errorKey]);

  // Load the scoring coefficients once (they only change with a deployment)
  const loadConfig = useCallback(async () => {
    try {
      const cfg = await getRankScoringConfig();
      setConfig(cfg);
    } catch (err) {
      // Documentation block simply stays hidden if the config cannot be loaded
      console.error("Failed to load rookie scoring config:", err);
    }
  }, []);

  useEffect(() => {
    loadBoard(false);
    loadConfig();
  }, [loadBoard, loadConfig]);

  // Client-side username filter — ranks stay GLOBAL (filtering never re-ranks)
  const filteredRookies = useMemo(() => {
    const term = searchTerm.trim().toLocaleLowerCase("tr");
    if (!term) return rookies;
    return rookies.filter((r) => r.username.toLocaleLowerCase("tr").includes(term));
  }, [rookies, searchTerm]);

  // Clicking a new column starts it at its natural direction (see
  // defaultSortDirection); clicking the already-active column flips it.
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(defaultSortDirection(SORT_COLUMN_TYPES[field]));
    }
  };

  // Neutral / ascending / descending indicator — same icon set already
  // used across the other admin panels (Users, Clubs, Teams, People, ...).
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <FaSort className="sort-icon" />;
    return sortDir === "asc" ? (
      <FaSortUp className="sort-icon active" />
    ) : (
      <FaSortDown className="sort-icon active" />
    );
  };

  // Sort is applied AFTER the username filter above. The board is always
  // loaded in full (no backend pagination), so sorting client-side keeps
  // this instant and avoids adding sort params to the API.
  const sortedRookies = useMemo(() => {
    const type = SORT_COLUMN_TYPES[sortField];
    const withDirection = (result: number) => (sortDir === "asc" ? result : -result);

    return [...filteredRookies].sort((a, b) => {
      if (type === "string") {
        // Turkish-aware comparison, consistent with the username filter above
        return withDirection(a[sortField].toString().localeCompare(b[sortField].toString(), "tr"));
      }
      if (type === "date") {
        return withDirection(new Date(a[sortField] as string).getTime() - new Date(b[sortField] as string).getTime());
      }
      // Rank column: 0 means "unranked" (below the entry threshold) — treat it
      // as the largest possible rank so unranked rows always sort AFTER the
      // ranked block, in both ascending and descending direction.
      if (sortField === "rank") {
        const ra = a.rank === 0 ? Number.MAX_SAFE_INTEGER : a.rank;
        const rb = b.rank === 0 ? Number.MAX_SAFE_INTEGER : b.rank;
        return withDirection(ra - rb);
      }
      return withDirection((a[sortField] as number) - (b[sortField] as number));
    });
  }, [filteredRookies, sortField, sortDir]);

  // Navigate to the rookie's public profile
  const handleUserClick = (rookie: RankBoardEntryDto) => {
    navigate(generateProfileUrl(rookie.username, rookie.userId));
  };

  // Format the online ratio (0..1) as a percentage
  const formatOnlineRatio = (ratio: number): string => `${Math.round(ratio * 100)}%`;

  /**
   * Topic suggestion approval rate as a percentage. The backend sends 0.0 when
   * no suggestion has been decided yet (all pending, or none submitted), which
   * is not the same as a genuine 0% record — those rows show a dash instead.
   */
  const formatApprovalRate = (row: RankBoardEntryDto): string => {
    const decided = row.topicSuggestionApprovedCount + row.topicSuggestionRejectedCount;
    if (decided === 0) return "—";
    return `${Math.round(row.topicSuggestionApprovalRate * 100)}%`;
  };

  // Compact score for display: 1234.5 → "1.2k". Full value goes in the tooltip.
  const formatScoreCompact = (score: number): string => {
    const abs = Math.abs(score);
    if (abs >= 1000) {
      return `${(score / 1000).toFixed(1)}k`;
    }
    return score.toFixed(1);
  };

  // Score cell color by sign
  const scoreClass = (score: number): string =>
    score >= 0 ? "rookie-score rookie-score--positive" : "rookie-score rookie-score--negative";

  return (
    <div className="admin-rank-board">
      {/* ── Header: title + refresh button ─────────────────────────────── */}
      <div className="admin-rank-board-header">
        <div className="admin-rank-board-title-wrap">
          <FaTrophy className="admin-rank-board-title-icon" />
          <h2 className="admin-rank-board-title">{t(titleKey)}</h2>
          <span className="admin-rank-board-count">
            {t(countKey, { count: rookies.length })}
          </span>
        </div>
        <div className="admin-rank-board-header-actions">
          {lastRefreshedAt && (
            <span className="admin-rank-board-refreshed-at">
              {t("admin_rookies_last_refreshed", { time: formatRefreshTime(lastRefreshedAt) })}
            </span>
          )}
          <AdminRefreshButton
            onClick={() => loadBoard(true)}
            refreshing={refreshing}
            disabled={loading}
            label={t("admin_rookies_refresh")}
          />
        </div>
      </div>

      {/* ── Scoring documentation (collapsible, visible to admin + mods) ── */}
      {config && (
        <div className="rookie-doc">
          <button className="rookie-doc-toggle" onClick={() => setDocOpen(!docOpen)}>
            <FaBook className="rookie-doc-icon" />
            <span>{t("admin_rookies_doc_title")}</span>
            {docOpen ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {docOpen && (
            <div className="rookie-doc-body">
              <p className="rookie-doc-intro">{t("admin_rookies_doc_intro")}</p>

              <div className="rookie-doc-section">
                <h4>{t("admin_rookies_doc_quality")}</h4>
                <ul>
                  <li>
                    {t("admin_rookies_doc_length", {
                      weight: config.lengthWeight,
                      target: config.targetAvgEntryLength,
                    })}
                  </li>
                  <li>
                    {t("admin_rookies_doc_online", {
                      weight: config.onlineWeight,
                      rampDays: config.onlineRampDays,
                    })}
                  </li>
                  <li className="rookie-doc-example">
                    {t("admin_rookies_doc_online_example", {
                      weight: config.onlineWeight,
                      rampDays: config.onlineRampDays,
                      // 2-day member, 100% ratio: (2 / rampDays) * weight
                      example2day: ((2 / config.onlineRampDays) * config.onlineWeight).toFixed(1),
                      // 60-day member, 50% ratio: full factor -> 0.5 * weight
                      example60day: (0.5 * config.onlineWeight).toFixed(1),
                    })}
                  </li>
                </ul>
              </div>

              <div className="rookie-doc-section">
                <h4>{t("admin_rookies_doc_reception")}</h4>
                <ul>
                  <li>{t("admin_rookies_doc_fav", { weight: config.favWeight })}</li>
                  <li>{t("admin_rookies_doc_up", { weight: config.upvoteWeight })}</li>
                </ul>
              </div>

              <div className="rookie-doc-section">
                <h4>{t("admin_rookies_doc_quantity")}</h4>
                <ul>
                  <li>
                    {t("admin_rookies_doc_entry", {
                      weight: config.entryWeight,
                      cap: config.entryCountCap,
                    })}
                  </li>
                </ul>
              </div>

              <div className="rookie-doc-section">
                <h4>{t("admin_rookies_doc_penalties")}</h4>
                <ul>
                  <li>{t("admin_rookies_doc_down", { penalty: config.downvotePenalty })}</li>
                  <li>{t("admin_rookies_doc_report", { penalty: config.reportPenalty })}</li>
                  <li>{t("admin_rookies_doc_edit", { penalty: config.adminEditPenalty })}</li>
                  <li>{t("admin_rookies_doc_delete", { penalty: config.adminDeletePenalty })}</li>
                </ul>
              </div>

              <p className="rookie-doc-formula">{t("admin_rookies_doc_formula")}</p>
              <p className="rookie-doc-note">
                {t(isAuthors ? "admin_authors_doc_rank_note" : "admin_rookies_doc_rank_note")}
              </p>
              {/* Entry threshold rule applies to the rookies board only —
                  the authors board ranks every candidate */}
              {!isAuthors && (
                <p className="rookie-doc-note">
                  {t("admin_rookies_doc_min_entries", {
                    required: config.rookieRankMinEntries,
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Search filter — always visible, like other admin panels ─────── */}
      <div className="admin-rank-board-filter">
        <div className="admin-rank-board-search-wrap">
          <FaSearch className="admin-rank-board-search-icon" />
          <input
            type="text"
            className="admin-rank-board-search-input"
            placeholder={t("admin_rookies_search_placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="admin-rank-board-search-clear"
              onClick={() => setSearchTerm("")}
              aria-label={t("common_clear", "Temizle")}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* ── Error / loading / empty states ─────────────────────────────── */}
      {error && <div className="admin-rank-board-error">{error}</div>}

      {loading ? (
        <div className="admin-rank-board-loading">{t(loadingKey)}</div>
      ) : rookies.length === 0 && !error ? (
        <div className="admin-rank-board-empty">{t(emptyKey)}</div>
      ) : filteredRookies.length === 0 ? (
        <div className="admin-rank-board-empty">{t("admin_rookies_no_results")}</div>
      ) : (
        /* ── Ranked board table ──────────────────────────────────────────
           The wrapper uses the rotateX(180deg) trick so the horizontal
           scrollbar renders ABOVE the header row instead of below the
           last row — much easier to reach on long lists. */
        <div className="admin-rank-board-table-wrap">
          {/* The authors board renders 3 extra gündem-suggestion columns, so it
              needs its own fixed-layout width set (see AdminRankBoard.css v11). */}
          <table className={`rookies-table ${isAuthors ? "rookies-table--with-suggestions" : ""}`}>
            <thead>
              <tr>
                {/* Compact headers — full names live in the title tooltips.
                    Every header is clickable to sort; see handleSort/getSortIcon above. */}
                <th
                  className="col-rank sortable-col"
                  title={t("admin_rookies_col_rank")}
                  onClick={() => handleSort("rank")}
                >
                  {t("admin_rookies_col_rank")} {getSortIcon("rank")}
                </th>
                {/* SCORE sits right next to rank per admin workflow */}
                <th
                  className="col-score sortable-col"
                  title={t("admin_rookies_col_score")}
                  onClick={() => handleSort("score")}
                >
                  {t("admin_rookies_col_score")} {getSortIcon("score")}
                </th>
                <th
                  className="col-user sortable-col"
                  title={t("admin_rookies_col_user")}
                  onClick={() => handleSort("username")}
                >
                  {t("admin_rookies_col_user")} {getSortIcon("username")}
                </th>
                <th
                  className="col-membership sortable-col"
                  title={t("admin_rookies_col_membership")}
                  onClick={() => handleSort("registrationDate")}
                >
                  {t("admin_rookies_col_membership")} {getSortIcon("registrationDate")}
                </th>
                <th
                  className="col-entries sortable-col"
                  title={t("admin_rookies_col_entries")}
                  onClick={() => handleSort("totalEntryCount")}
                >
                  {t("admin_rookies_col_entries_short")} {getSortIcon("totalEntryCount")}
                </th>
                <th
                  className="sortable-col"
                  title={t("admin_rookies_col_avg_length")}
                  onClick={() => handleSort("avgEntryLength")}
                >
                  {t("admin_rookies_col_avg_length_short")} {getSortIcon("avgEntryLength")}
                </th>
                {/* Vote header is icons-only; each icon sorts its own metric
                    independently since the column combines 3 different values */}
                <th title={t("admin_rookies_col_votes")}>
                  <span className="rookie-votes-header">
                    <span className="rookie-vote-sort" onClick={() => handleSort("upvotes")}>
                      <FaThumbsUp className="rookie-vote--up" />
                      {getSortIcon("upvotes")}
                    </span>
                    <span className="rookie-vote-sort" onClick={() => handleSort("downvotes")}>
                      <FaThumbsDown className="rookie-vote--down" />
                      {getSortIcon("downvotes")}
                    </span>
                    <span className="rookie-vote-sort" onClick={() => handleSort("favorites")}>
                      <FaStar className="rookie-vote--fav" />
                      {getSortIcon("favorites")}
                    </span>
                  </span>
                </th>
                <th
                  className="sortable-col"
                  title={t("admin_rookies_col_login_days")}
                  onClick={() => handleSort("totalLoginDays")}
                >
                  {t("admin_rookies_col_login_days_short")} {getSortIcon("totalLoginDays")}
                </th>
                <th
                  className="sortable-col"
                  title={t("admin_rookies_col_streak")}
                  onClick={() => handleSort("currentStreak")}
                >
                  {t("admin_rookies_col_streak")} {getSortIcon("currentStreak")}
                </th>
                <th
                  className="sortable-col"
                  title={t("admin_rookies_col_online_ratio")}
                  onClick={() => handleSort("onlineRatio")}
                >
                  {t("admin_rookies_col_online_ratio")} {getSortIcon("onlineRatio")}
                </th>
                <th
                  className="sortable-col"
                  title={t("admin_rookies_col_reports")}
                  onClick={() => handleSort("reportCount")}
                >
                  {t("admin_rookies_col_reports")} {getSortIcon("reportCount")}
                </th>
                <th
                  className="sortable-col"
                  title={t("admin_rookies_col_admin_edited")}
                  onClick={() => handleSort("adminEditedCount")}
                >
                  {t("admin_rookies_col_admin_edited_short")} {getSortIcon("adminEditedCount")}
                </th>
                <th
                  className="sortable-col"
                  title={t("admin_rookies_col_admin_deleted")}
                  onClick={() => handleSort("adminDeletedCount")}
                >
                  {t("admin_rookies_col_admin_deleted_short")} {getSortIcon("adminDeletedCount")}
                </th>

                {/* Custom topic (gündem) suggestion columns — authors board only */}
                {isAuthors && (
                  <>
                    <th
                      className="sortable-col"
                      title={t("admin_rookies_col_topic_suggestions")}
                      onClick={() => handleSort("topicSuggestionCount")}
                    >
                      {t("admin_rookies_col_topic_suggestions_short")} {getSortIcon("topicSuggestionCount")}
                    </th>
                    <th
                      className="sortable-col"
                      title={t("admin_rookies_col_topic_suggestions_rejected")}
                      onClick={() => handleSort("topicSuggestionRejectedCount")}
                    >
                      {t("admin_rookies_col_topic_suggestions_rejected_short")} {getSortIcon("topicSuggestionRejectedCount")}
                    </th>
                    <th
                      className="sortable-col"
                      title={t("admin_rookies_col_topic_suggestions_rate")}
                      onClick={() => handleSort("topicSuggestionApprovalRate")}
                    >
                      {t("admin_rookies_col_topic_suggestions_rate_short")} {getSortIcon("topicSuggestionApprovalRate")}
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedRookies.map((rookie) => (
                <tr key={rookie.userId} className={rookie.rank >= 1 && rookie.rank <= 3 ? "rookie-top-row" : ""}>
                  {/* Rank — top 3 get a highlighted badge. rank 0 = below the
                      ranking entry threshold: shown as a dash with a tooltip
                      explaining how many entries are required. */}
                  <td className="col-rank">
                    {rookie.rank === 0 ? (
                      <span
                        className="rookie-rank-badge rookie-rank-badge--unranked"
                        title={t("admin_rookies_unranked_tooltip", {
                          required: config?.rookieRankMinEntries ?? "",
                          count: rookie.totalEntryCount,
                        })}
                      >
                        —
                      </span>
                    ) : (
                      <span className={`rookie-rank-badge ${rookie.rank <= 3 ? `rookie-rank-badge--top${rookie.rank}` : ""}`}>
                        {rookie.rank}
                      </span>
                    )}
                  </td>

                  {/* SCORE — compact (1.2k) with exact value in the tooltip */}
                  <td className="col-score">
                    <span
                      className={scoreClass(rookie.score)}
                      title={t("admin_rookies_full_score", { score: rookie.score.toFixed(1) })}
                    >
                      {formatScoreCompact(rookie.score)}
                    </span>
                  </td>

                  {/* Username → profile link, ellipsized with full-name tooltip */}
                  <td className="col-user">
                    <button
                      className="rookie-username-link"
                      onClick={() => handleUserClick(rookie)}
                      title={rookie.username}
                    >
                      {rookie.username}
                    </button>
                  </td>

                  {/* Membership: registration date + auto-calculated day count */}
                  <td className="col-membership">
                    <div className="rookie-cell-stack">
                      <span>{formatAdminDate(rookie.registrationDate)}</span>
                      <span className="rookie-cell-sub">
                        {t("admin_rookies_membership_days", { count: rookie.membershipDays })}
                      </span>
                    </div>
                  </td>

                  {/* Entry counts: total + per-domain breakdown */}
                  <td className="col-entries">
                    <div className="rookie-cell-stack">
                      <span className="rookie-entry-total">{rookie.totalEntryCount}</span>
                      <span
                        className="rookie-cell-sub rookie-entry-breakdown"
                        title={`${t("admin_rookies_domain_topic")}: ${rookie.customTopicEntryCount} · ${t("admin_rookies_domain_match")}: ${rookie.matchEntryCount} · ${t("admin_rookies_domain_club")}: ${rookie.clubEntryCount} · ${t("admin_rookies_domain_person")}: ${rookie.personEntryCount} · ${t("admin_rookies_domain_team")}: ${rookie.teamEntryCount}`}
                      >
                        {/* Ultra-compact single-line breakdown — full domain
                            names live in the title tooltip above */}
                        {/* Order: Gündem, Maç, Kulüp, Kişi, Takım (product decision) */}
                        {t("admin_rookies_domain_topic_short")}: {rookie.customTopicEntryCount} ·{" "}
                        {t("admin_rookies_domain_match_short")}: {rookie.matchEntryCount} ·{" "}
                        {t("admin_rookies_domain_club_short")}: {rookie.clubEntryCount} ·{" "}
                        {t("admin_rookies_domain_person_short")}: {rookie.personEntryCount} ·{" "}
                        {t("admin_rookies_domain_team_short")}: {rookie.teamEntryCount}
                      </span>
                    </div>
                  </td>

                  {/* Average entry length (chars, all domains) */}
                  <td className="rookie-num">{rookie.avgEntryLength.toFixed(1)}</td>

                  {/* Votes received: + / − / fav */}
                  <td>
                    <div className="rookie-votes">
                      <span className="rookie-vote rookie-vote--up">
                        <FaThumbsUp /> {rookie.upvotes}
                      </span>
                      <span className="rookie-vote rookie-vote--down">
                        <FaThumbsDown /> {rookie.downvotes}
                      </span>
                      <span className="rookie-vote rookie-vote--fav">
                        <FaStar /> {rookie.favorites}
                      </span>
                    </div>
                  </td>

                  {/* Login stats */}
                  <td className="rookie-num">{rookie.totalLoginDays}</td>
                  <td className="rookie-num">{rookie.currentStreak}</td>
                  <td className="rookie-num">{formatOnlineRatio(rookie.onlineRatio)}</td>

                  {/* Moderation signals — highlighted when non-zero */}
                  <td className={`rookie-num ${rookie.reportCount > 0 ? "rookie-warn" : ""}`}>
                    {rookie.reportCount}
                  </td>
                  <td className={`rookie-num ${rookie.adminEditedCount > 0 ? "rookie-warn" : ""}`}>
                    {rookie.adminEditedCount}
                  </td>
                  <td className={`rookie-num ${rookie.adminDeletedCount > 0 ? "rookie-warn" : ""}`}>
                    {rookie.adminDeletedCount}
                  </td>

                  {/* Custom topic (gündem) suggestions — authors board only */}
                  {isAuthors && (
                    <>
                      <td className="rookie-num">{rookie.topicSuggestionCount}</td>
                      <td className={`rookie-num ${rookie.topicSuggestionRejectedCount > 0 ? "rookie-warn" : ""}`}>
                        {rookie.topicSuggestionRejectedCount}
                      </td>
                      {/* A dash instead of "0%" when nothing has been reviewed yet,
                          so an untouched backlog is not mistaken for a 0% record. */}
                      <td className="rookie-num">
                        {formatApprovalRate(rookie)}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};